import { CSSProperties, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FilterToastItem } from '../hooks/useFilterToast';
import { formatFatalities } from './filterSummary';

interface FilterToastStackProps {
    toasts: FilterToastItem[];
    /** Only offered when the graph section is off screen. */
    showJumpLink: boolean;
    onJump: () => void;
    onDismiss: (id: number) => void;
    onPause: () => void;
    onResume: () => void;
}

function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// This renders into document.body, which puts it outside
// #fatalities-dashboard-container -- so every rule in index.scss stops applying
// and the styling has to be self-contained.
const stackStyle: CSSProperties = {
    position: 'fixed',
    right: 16,
    bottom: 16,
    width: 380,
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    display: 'flex',
    // Oldest at the top, newest nearest the corner where the eye already is.
    flexDirection: 'column',
    gap: 8,
    // Clears Igloo's sticky header (.top, z-index 500 in css/ig-header.css) and
    // its page-loading bar (99998/99999 in css/ig-base.css). The skip link sits
    // at 999999999 and should stay on top of us.
    zIndex: 100000,
    fontFamily: 'inherit',
    fontSize: 15,
    lineHeight: 1.4,
    textAlign: 'left',
};

const panelStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px',
    borderRadius: 6,
    backgroundColor: '#0c539a',
    color: '#ffffff',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
};

const checkStyle: CSSProperties = {
    color: '#8fe388',
    fontSize: 17,
    lineHeight: 1.3,
    flexShrink: 0,
};

const linkStyle: CSSProperties = {
    display: 'inline-block',
    marginTop: 6,
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#ffffff',
    font: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
};

const dismissStyle: CSSProperties = {
    flexShrink: 0,
    marginLeft: 'auto',
    padding: '0 2px',
    border: 'none',
    background: 'none',
    color: '#ffffff',
    fontSize: 20,
    lineHeight: 1,
    opacity: 0.75,
    cursor: 'pointer',
};

interface ToastRowProps {
    toast: FilterToastItem;
    showJumpLink: boolean;
    onJump: () => void;
    onDismiss: (id: number) => void;
}

const ToastRow = ({ toast, showJumpLink, onJump, onDismiss }: ToastRowProps) => {
    const [entered, setEntered] = useState(false);
    const reduceMotion = prefersReducedMotion();

    useEffect(() => {
        const frame = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    const style: CSSProperties = reduceMotion
        ? panelStyle
        : {
            ...panelStyle,
            opacity: entered ? 1 : 0,
            transform: entered ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 160ms ease-out, transform 160ms ease-out',
        };

    return (
        <div style={style}>
            <span style={checkStyle} aria-hidden="true">&#10003;</span>
            <div>
                <div>
                    {toast.summary} &mdash; <strong>{formatFatalities(toast.total)}</strong>
                </div>
                {showJumpLink && (
                    <button type="button" style={linkStyle} onClick={onJump}>
                        Jump to graphs
                    </button>
                )}
            </div>
            <button
                type="button"
                style={dismissStyle}
                onClick={() => onDismiss(toast.id)}
                aria-label="Dismiss notification"
            >
                &times;
            </button>
        </div>
    );
};

const FilterToastStack = ({
    toasts,
    showJumpLink,
    onJump,
    onDismiss,
    onPause,
    onResume,
}: FilterToastStackProps) => {
    if (toasts.length === 0) {
        return null;
    }

    return createPortal(
        <div
            style={stackStyle}
            role="status"
            aria-live="polite"
            onMouseEnter={onPause}
            onMouseLeave={onResume}
            onFocus={onPause}
            onBlur={onResume}
        >
            {toasts.map((toast, index) => (
                <ToastRow
                    key={toast.id}
                    toast={toast}
                    // One link on the newest toast only; repeating it down the
                    // stack is noise, since they all scroll to the same place.
                    showJumpLink={showJumpLink && index === toasts.length - 1}
                    onJump={onJump}
                    onDismiss={onDismiss}
                />
            ))}
        </div>,
        document.body
    );
};

export default FilterToastStack;
